import type { FC, PropsWithChildren } from 'react'
import styled from '@emotion/styled'

import { ChevronRightIcon } from '@segment/matcha/components/Icons/Common'
import ColumnTrack from '@segment/matcha/components/ColumnTrack'
import Badge from '@segment/matcha/components/Badge'

import CopyTextBase, * as CopyText from '@segment/matcha/recipes/CopyText'
import SectionBase, * as Section from '@segment/matcha/recipes/Section'
import CardBase, * as Card from '@segment/matcha/recipes/Card'

import getAbsoluteUrl from '@segment/utils/getAbsoluteUrl'

import { usePageConfig } from '@segment/contexts/page'

const copy = {
  heading: `Related content`,
}

const boxes = [
  {
    badge: `Guide`,
    title: `A practical guide to data privacy planning in 2021`,
    link: `https://learn.segment.com/data-privacy-ebook/`,
    body: `Information on consumer data privacy and changing laws and standards.`,
  },
  {
    badge: `Guide`,
    title: `Why first-party data is the key to data privacy`,
    link: `/resources/data-privacy/why-first-party-data-key-to-data-privacy/`,
    body: `First-party data will help build trust with people who visit your website and use your products.`,
  },
  {
    badge: `Webinar`,
    title: `Demystifying data residency with Segment`,
    link: `https://learn.segment.com/data-residency-emea/`,
    body: `Protecting personal data and helping our customers navigate new regulations.`,
  },
  {
    badge: `Guide`,
    title: `How to activate & leverage your data`,
    link: `/blog/first-party-data-guide/`,
    body: `Construct a data strategy to deliver private, respectful, and personalized experiences.`,
  },
]

const RelatedContent: FC<PropsWithChildren<unknown>> = () => {
  const pageConfig = usePageConfig()
  return (
    <StyledSection variant="centered">
      <Section.Copy>
        <CopyTextBase centered={true}>
          <CopyText.Heading>{copy.heading}</CopyText.Heading>
        </CopyTextBase>
      </Section.Copy>
      <Section.Contents>
        <Body>
          <ColumnTrack columns={1} overrides={{ md: 2 }}>
            {boxes.map((card, index) => (
              <CardBase key={index} href={getAbsoluteUrl(card.link, pageConfig)}>
                <Card.Body>
                  {card.badge && (
                    <Card.Accessories>
                      <Card.AccessoriesRow>
                        <Badge variant="outlined">{card.badge}</Badge>
                      </Card.AccessoriesRow>
                    </Card.Accessories>
                  )}
                  <Card.Copy>
                    <Card.Headline>{card.title}</Card.Headline>
                    <Card.Description>{card.body}</Card.Description>
                  </Card.Copy>
                  <Card.Action alignment="right">
                    <ChevronRightIcon />
                  </Card.Action>
                </Card.Body>
              </CardBase>
            ))}
          </ColumnTrack>
        </Body>
      </Section.Contents>
    </StyledSection>
  )
}

const StyledSection = styled(SectionBase)`
  row-gap: 40px;
`

const Body = styled.div`
  max-width: 856px;
  margin: -40px auto 0 auto;

  ${({ theme }) => theme.breakpoints.up(`md`)} {
    margin-top: 0;
  }
`

export default RelatedContent
